<template>
  <v-container fluid>
    <v-card class="mb-2">
      <v-card-title>Редактирование отчёта маржинальности №{{ reportId }}</v-card-title>
    </v-card>

    <v-overlay :value="pageLoading" absolute opacity="0.35" z-index="5">
      <v-progress-circular indeterminate size="64" color="primary" />
    </v-overlay>

    <div class="row my-2">
      <div class="col">
        <v-card>
          <v-card-title>Данные по проекту</v-card-title>
          <v-card-text>
            <project-selector
              v-if="initialized"
              :key="projectSelectorKey"
              :value="project"
              single
              no-rules
              @input="onProjectInput"
            />

            <v-select
              v-if="initialized"
              v-model="selectedPackage"
              :items="packages.items"
              item-text="title"
              item-value="_idx"
              label="Пакет"
              :loading="packages.loading"
              :disabled="pageLoading || packages.loading || !packages.items.length"
              no-data-text="Пакетов не найдено"
              return-object
            />

            <div v-if="selectedPackage" class="mb-2">
              <v-chip
                v-for="(campaign, i) in selectedPackage.campaigns"
                :key="i"
                small
                class="mr-1 mb-1"
              >{{ campaign }}</v-chip>
            </div>

            <div class="row">
              <div class="col">
                <v-menu
                  v-model="menuDateStart"
                  :close-on-content-click="true"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template #activator="{ on, attrs }">
                    <v-text-field
                      :value="period[0]"
                      label="Дата начала"
                      prepend-icon="mdi-calendar"
                      readonly
                      :disabled="!selectedPackage"
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    v-model="period[0]"
                    :min="selectedPackage && selectedPackage.started_at"
                    :max="period[1]"
                    locale="ru-RU"
                    @change="menuDateStart = false"
                  />
                </v-menu>
              </div>
              <div class="col">
                <v-menu
                  v-model="menuDateEnd"
                  :close-on-content-click="true"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template #activator="{ on, attrs }">
                    <v-text-field
                      :value="period[1]"
                      label="Дата окончания"
                      prepend-icon="mdi-calendar"
                      readonly
                      :disabled="!selectedPackage"
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    v-model="period[1]"
                    :min="period[0]"
                    :max="packageEndDate"
                    locale="ru-RU"
                    @change="menuDateEnd = false"
                  />
                </v-menu>
              </div>
            </div>

            <v-simple-table dense>
              <tbody>
                <tr>
                  <th style="user-select: revert;">Цена минуты телефонии</th>
                  <td>
                    <div class="float-right">
                      <input v-model="payload.minuteCost" type="text" class="input-border">
                    </div>
                  </td>
                </tr>
                <tr>
                  <th style="user-select: revert;">Админ расход</th>
                  <td>
                    <div class="float-right">
                      <input v-model="payload.administrativeExpensesPercent" type="number" class="input-border">
                    </div>
                  </td>
                </tr>
                <tr>
                  <th style="user-select: revert;">ФОТ операторов</th>
                  <td>
                    <div class="float-right">
                      <input v-model="payload.operatorPayroll" type="text" class="input-border">
                    </div>
                  </td>
                </tr>
                <tr>
                  <th style="user-select: revert;">ФОТ МП</th>
                  <td>
                    <div class="float-right">
                      <input v-model="payload.pmPayroll" type="text" class="input-border">
                    </div>
                  </td>
                </tr>
                <tr>
                  <th style="user-select: revert;">ФОТ РГ</th>
                  <td>
                    <div class="float-right">
                      <input v-model="payload.glPayroll" type="text" class="input-border">
                    </div>
                  </td>
                </tr>
                <tr>
                  <th>Создан</th>
                  <td>
                    <div class="float-right readonly-value">{{ formatDate(report.created_at) }}</div>
                  </td>
                </tr>
                <tr>
                  <th>Обновлён</th>
                  <td>
                    <div class="float-right readonly-value">{{ formatDate(report.updated_at) }}</div>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>

            <div class="mt-3">
              <v-btn
                color="success"
                size="small"
                block
                :disabled="calculating || pageLoading || !project.length || !selectedPackage"
                :loading="calculating"
                @click="submit"
              >
                Рассчитать
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </div>

      <div v-if="callsStatistics.length > 0" class="col">
        <v-card>
          <v-card-title class="border-bottom">Статистика звонков</v-card-title>
          <v-card-text>
            <v-simple-table dense>
              <tbody>
                <tr v-for="item in callsStatistics" :key="item.label">
                  <th style="user-select: revert;">
                    <v-tooltip v-if="item.description" bottom max-width="400">
                      <template #activator="{ on }">
                        <span v-on="on">{{ item.label }}</span>
                      </template>
                      <span v-text="item.description" />
                    </v-tooltip>
                    <span v-else>{{ item.label }}</span>
                  </th>
                  <td>
                    <div class="float-right">{{ item.value }}</div>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card-text>
        </v-card>
      </div>

      <div v-if="margin.length > 0" class="col">
        <v-card>
          <v-card-title>Маржинальность</v-card-title>
          <v-card-text>
            <v-simple-table dense>
              <tbody>
                <tr v-for="item in margin" :key="item.label">
                  <th style="user-select: revert;">
                    <v-tooltip v-if="item.description" bottom max-width="400">
                      <template #activator="{ on }">
                        <span v-on="on">{{ item.label }}</span>
                      </template>
                      <span v-text="item.description" />
                    </v-tooltip>
                    <span v-else>{{ item.label }}</span>
                  </th>
                  <td>
                    <div class="float-right">
                      <v-badge
                        v-if="item.color && item.color !== ''"
                        :color="item.color"
                        :content="item.value"
                        inline
                      />
                      <span v-else>{{ item.value }}</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card-text>
        </v-card>
      </div>
    </div>

    <v-row v-if="hasResults" class="mt-2">
      <v-col cols="12" class="d-flex justify-space-between">
        <v-btn color="error" text :loading="deleting" :disabled="pageLoading" @click="deleteDialog = true">
          Удалить
        </v-btn>
        <div>
          <v-btn class="mr-2" :disabled="pageLoading || saving" @click="goBack">Отмена</v-btn>
          <v-btn color="primary" :loading="saving" :disabled="pageLoading" @click="onSubmit">Сохранить</v-btn>
        </div>
      </v-col>
    </v-row>

    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title>Удалить отчёт?</v-card-title>
        <v-card-text>Отчёт №{{ reportId }} будет удалён без возможности восстановления.</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteDialog = false">Отмена</v-btn>
          <v-btn color="error" :loading="deleting" @click="onDelete">Удалить</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import ProjectSelector from '~/components/partials/selectors/ProjectSelector.vue'

const CALCULATED_FIELDS = [
  { key: 'overage_minutes', label: 'Минуты перерасхода' },
  { key: 'overage_rub', label: 'Перерасход, руб.' },
  { key: 'total_duration_min', label: 'Общая длительность, мин' },
  { key: 'talk_duration_min', label: 'Длительность разговоров, мин' },
  { key: 'talk_duration_under_15_min', label: 'Разговоры до 15 сек, мин' },
  { key: 'paid_minutes', label: 'Оплаченные минуты' },
  { key: 'total_calls', label: 'Всего звонков' },
  { key: 'paid_minute_cost', label: 'Стоимость оплаченной минуты' },
  { key: 'margin_excluding_admin_expenses', label: 'Маржа без админ. расходов' },
  { key: 'margin_excluding_admin_expenses_percent', label: 'Маржа без админ. расходов, %' },
  { key: 'margin', label: 'Маржа' },
  { key: 'margin_percent', label: 'Маржа, %' },
]

const LABEL_TO_KEY = Object.fromEntries(CALCULATED_FIELDS.map((f) => [f.label, f.key]))

export default {
  name: 'SavedMarginReportEdit',
  components: {
    ProjectSelector,
  },

  data() {
    return {
      reportId: null,
      report: {},
      initialized: false,
      isInitializing: false,
      packagesProjectId: null,
      projectSelectorKey: 0,
      pageLoading: true,
      calculating: false,
      saving: false,
      deleting: false,
      deleteDialog: false,

      project: [],
      selectedPackage: null,
      packages: {
        items: [],
        loading: false,
      },
      period: [null, null],
      menuDateStart: false,
      menuDateEnd: false,

      margin: [],
      callsStatistics: [],
      calculatedForm: {},

      payload: {
        minuteCost: 0,
        administrativeExpensesPercent: 0,
        glPayroll: 0,
        pmPayroll: 0,
        operatorPayroll: 0,
      },
    }
  },

  computed: {
    packageEndDate() {
      if (!this.selectedPackage) return null
      return this.selectedPackage.closed_at ?? new Date().toISOString().slice(0, 10)
    },

    hasResults() {
      return this.callsStatistics.length > 0 && this.margin.length > 0
    },
  },

  watch: {
    selectedPackage(pkg, oldPkg) {
      if (this.isInitializing) return
      if (!pkg) {
        this.period = [null, null]
        return
      }
      if (oldPkg && pkg.id === oldPkg.id) return
      const today = new Date().toISOString().slice(0, 10)
      this.period = [pkg.started_at, pkg.closed_at ?? today]
    },
  },

  async mounted() {
    const reportId = this.$route.params.id
    if (!reportId) {
      this.notifyError('Не указан ID отчёта')
      this.goBack()
      return
    }

    this.reportId = reportId
    await this.loadReport()
  },

  methods: {
    ...mapActions({
      fetchFilterOptions: 'otm/saved_margin_reports/fetchFilterOptions',
      loadProjectPackages: 'otm/calculator/loadProjectPackages',
      loadPackageCallStatistics: 'otm/calculator/loadPackageCallStatistics',
      calculatePackageMargin: 'otm/calculator/calculatePackageMargin',
      fetchReport: 'otm/saved_margin_reports/fetchReport',
      updateReport: 'otm/saved_margin_reports/updateReport',
      deleteReport: 'otm/saved_margin_reports/deleteReport',
    }),

    notifyError(message) {
      this.$store.commit('shared/SET_ERROR', { message }, { root: true })
    },

    notifySuccess(message) {
      this.$store.commit('shared/SET_SUCCESS', { message }, { root: true })
    },

    formatDate(value) {
      if (!value) return '—'
      return String(value).slice(0, 16).replace('T', ' ')
    },

    fillCalculatedForm(report) {
      const form = {}
      for (const field of CALCULATED_FIELDS) {
        form[field.key] = report[field.key]
      }
      this.calculatedForm = form
    },

    syncCalculatedFormFromItems(items) {
      const form = { ...this.calculatedForm }
      for (const item of items) {
        const key = LABEL_TO_KEY[item.label]
        if (!key) continue
        const numeric = Number(String(item.value).replace(/\s/g, '').replace(',', '.'))
        form[key] = Number.isNaN(numeric) ? item.value : numeric
      }
      this.calculatedForm = form
    },

    onProjectInput(nextProject) {
      if (this.isInitializing) {
        this.project = nextProject
        return
      }

      const newId = nextProject.length ? Number(nextProject[0]) : null
      const oldId = this.project.length ? Number(this.project[0]) : null

      this.project = nextProject

      if (newId === oldId) return

      this.selectedPackage = null
      this.packages.items = []
      this.packagesProjectId = null
      this.projectSelectorKey = newId || Date.now()

      if (!newId) return

      this.loadPackagesForProject(newId, { force: true })
    },

    async loadPackagesForProject(projectId, { force = false } = {}) {
      const normalizedId = Number(projectId)
      if (!force && normalizedId === this.packagesProjectId && this.packages.items.length) {
        return
      }

      this.packages.loading = true
      try {
        const items = await this.loadProjectPackages(normalizedId)
        this.packagesProjectId = normalizedId
        this.packages.items = items.map((item, idx) => ({ ...item, _idx: idx }))
      } catch (err) {
        this.packages.items = []
        this.packagesProjectId = null
        this.notifyError('Ошибка загрузки пакетов')
        // eslint-disable-next-line no-console
        console.error(err)
      } finally {
        this.packages.loading = false
      }
    },

    getProjectId(report) {
      if (report.project_id != null) return Number(report.project_id)
      if (report.project != null && typeof report.project === 'object' && report.project.id != null) {
        return Number(report.project.id)
      }
      if (report.project != null && typeof report.project !== 'object') {
        return Number(report.project)
      }
      return null
    },

    findProjectIdInFilterOptions(report, filterOptions) {
      for (const project of filterOptions) {
        const hasPackage = (project.packages || []).some((pkg) => (
          Number(pkg.id) === Number(report.package)
        ))
        if (hasPackage) return Number(project.id)
      }
      return null
    },

    selectPackageFromReport(report) {
      const match = this.packages.items.find((pkg) => (
        Number(pkg.id) === Number(report.package)
        && Number(pkg.contract_id) === Number(report.contract)
        && Number(pkg.appendix_id) === Number(report.appendix)
      )) || this.packages.items.find((pkg) => Number(pkg.id) === Number(report.package))

      if (!match) return false

      this.selectedPackage = match
      return true
    },

    applyPackageFallback(report) {
      const packageFromReport = {
        id: Number(report.package),
        contract_id: Number(report.contract),
        appendix_id: Number(report.appendix),
        title: report.package_title || report.package_name || `Пакет №${report.package}`,
        campaigns: report.campaigns || [],
        started_at: Array.isArray(report.period) ? report.period[0] : null,
        closed_at: Array.isArray(report.period) ? report.period[1] : null,
        _idx: 0,
      }

      this.packages.items = [packageFromReport]
      this.selectedPackage = packageFromReport
    },

    async setupProjectAndPackages(report) {
      let projectId = this.getProjectId(report)

      if (!projectId) {
        const filterOptions = await this.fetchFilterOptions()
        projectId = this.findProjectIdInFilterOptions(report, filterOptions)
      }

      if (projectId) {
        await this.loadPackagesForProject(projectId, { force: true })
        this.project = [projectId]
        this.projectSelectorKey = projectId

        if (!this.selectPackageFromReport(report)) {
          this.applyPackageFallback(report)
        }
        return
      }

      this.applyPackageFallback(report)
      this.notifyError('Не удалось определить проект — выберите проект вручную')
    },

    fillFormFromReport(report) {
      this.period = Array.isArray(report.period) ? [...report.period] : [null, null]
      this.payload = {
        minuteCost: report.minute_cost,
        administrativeExpensesPercent: report.administrative_expenses_percent,
        operatorPayroll: report.operator_payroll,
        pmPayroll: report.pm_payroll,
        glPayroll: report.gl_payroll,
      }
      this.fillCalculatedForm(report)
    },

    buildPatchPayload() {
      if (!this.selectedPackage) {
        throw new Error('Пакет не выбран')
      }

      const { id: packageId, contract_id: contractId, appendix_id: appendixId } = this.selectedPackage

      const payload = {
        contract: Number(contractId),
        appendix: Number(appendixId),
        package: Number(packageId),
        period: this.period,
        minute_cost: Number(this.payload.minuteCost),
        operator_payroll: Number(this.payload.operatorPayroll),
        pm_payroll: Number(this.payload.pmPayroll),
        gl_payroll: Number(this.payload.glPayroll),
        administrative_expenses_percent: Number(this.payload.administrativeExpensesPercent),
      }

      for (const field of CALCULATED_FIELDS) {
        payload[field.key] = Number(this.calculatedForm[field.key])
      }

      return payload
    },

    async runCalculate() {
      if (!this.selectedPackage) return

      this.calculating = true
      this.margin = []
      this.callsStatistics = []

      const { id: packageId, contract_id: contractId, appendix_id: appendixId } = this.selectedPackage

      try {
        const [marginItems, callsItems] = await Promise.all([
          this.calculatePackageMargin({
            contractId,
            appendixId,
            packageId,
            period: this.period,
            ...this.payload,
          }),
          this.loadPackageCallStatistics({
            contractId,
            appendixId,
            packageId,
            period: this.period,
          }),
        ])

        this.margin = marginItems || []
        this.callsStatistics = callsItems || []
        this.syncCalculatedFormFromItems([...this.callsStatistics, ...this.margin])
      } catch (err) {
        this.notifyError('Ошибка расчёта')
        // eslint-disable-next-line no-console
        console.error(err)
      } finally {
        this.calculating = false
      }
    },

    submit() {
      this.runCalculate()
    },

    async loadReport() {
      this.pageLoading = true
      this.isInitializing = true
      this.initialized = false
      try {
        const report = await this.fetchReport(this.reportId)
        this.report = report
        this.fillFormFromReport(report)
        await this.setupProjectAndPackages(report)

        if (this.selectedPackage) {
          await this.runCalculate()
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err)
        this.notifyError('Ошибка загрузки отчёта')
        this.goBack()
      } finally {
        this.isInitializing = false
        this.pageLoading = false
        this.initialized = true
      }
    },

    async onSubmit() {
      if (!this.hasResults) return

      this.saving = true
      try {
        const response = await this.updateReport({
          reportId: this.reportId,
          data: this.buildPatchPayload(),
        })
        this.notifySuccess((response && response.message) || 'Отчёт изменён!')
        this.goBack()
      } catch (err) {
        const message = (err.response && err.response.data && err.response.data.message) || 'Ошибка сохранения отчёта'
        this.notifyError(message)
      } finally {
        this.saving = false
      }
    },

    async onDelete() {
      this.deleting = true
      try {
        const response = await this.deleteReport(this.reportId)
        this.notifySuccess((response && response.message) || 'Отчёт удалён!')
        this.goBack()
      } catch (err) {
        const message = (err.response && err.response.data && err.response.data.message) || 'Ошибка удаления отчёта'
        this.notifyError(message)
      } finally {
        this.deleting = false
        this.deleteDialog = false
      }
    },

    goBack() {
      this.$router.push({ name: 'otm.saved-margin-reports.list' })
    },
  },
}
</script>

<style scoped lang="scss">
.input-border {
  text-align: right;
  width: 80px;
  padding: 0 10px;
  border-radius: 4px;
  outline: none;
  appearance: textfield;
  -moz-appearance: textfield;

  .theme--light & {
    color: rgba(0, 0, 0, 0.87);
    background: rgba(0, 0, 0, 0.04);
    border: 1px solid rgba(0, 0, 0, 0.24);
  }

  .theme--dark & {
    color: rgba(255, 255, 255, 0.87);
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.24);
  }
}

.input-border::-webkit-outer-spin-button,
.input-border::-webkit-inner-spin-button {
  -webkit-appearance: none;
}

.readonly-value {
  min-width: 80px;
  text-align: right;
  padding: 6px 10px;
  line-height: 20px;
}
</style>
